import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Avatar from '@material-ui/core/Avatar'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import FlagIcon from 'components/material-symbol/icons/Flag'
import PlaceIcon from 'components/material-symbol/icons/Place'
import ScheduleIcon from 'components/material-symbol/icons/Schedule'
import CalendarTodayIcon from 'components/material-symbol/icons/CalendarToday'
import { getInitials } from 'lib/text-display'
import { durationText } from 'utils/durationUtils'
import { getMemberLabel } from 'pages/schedule/SelectLessonTime/redesign/booking-summary-card'
import useChromeStyles from 'pages/schedule/SelectLessonTime/redesign/styles'
import CancellationCallout from 'pages/schedule/components/cancellation-callout'
import useStyles from './styles'

const CoachAvatar = ({ coach }) => {
  const chrome = useChromeStyles()
  const photo = coach.coachProfile?.profilePhoto
  return photo ? (
    <Avatar className={chrome.avatar} alt={coach.name} src={photo} />
  ) : (
    <Avatar className={chrome.avatar}>{getInitials(coach.name)}</Avatar>
  )
}

CoachAvatar.propTypes = { coach: PropTypes.object.isRequired }

// Step 3 flavour of the step 1 `BookingSummaryCard`: same coach header, lesson
// facts and total (it shares the step 1 style sheet and helpers), plus the full
// slot range and the coach's cancellation callout under the total.
const ReviewSummaryCard = ({
  lessonType,
  coach,
  facility: chosenFacility,
  slotLabel,
  totalLabel,
  totalNote,
  showCallout,
}) => {
  const chrome = useChromeStyles()
  const classes = useStyles()
  const facility = chosenFacility || lessonType.facilities?.[0]
  const memberLabel = getMemberLabel(coach.coachProfile)
  const cityState = [facility?.city, facility?.state].filter(Boolean).join(', ')

  return (
    <aside className={chrome.summaryCard} aria-label="Booking summary">
      <div className={chrome.coachRow}>
        <CoachAvatar coach={coach} />
        <div>
          <Typography variant="h6" component="p" className={chrome.coachName}>
            {coach.name}
          </Typography>
          {memberLabel && (
            <span className={chrome.coachTitle}>
              <Chip
                size="small"
                label={memberLabel}
                className={chrome.memberChip}
              />
            </span>
          )}
        </div>
      </div>
      <hr className={chrome.summaryDivider} />
      <Typography variant="h5" component="h2" className={chrome.lessonTitle}>
        {lessonType.title}
      </Typography>
      <ul className={chrome.facts}>
        {facility?.name && (
          <li className={chrome.fact}>
            <FlagIcon aria-hidden="true" />
            <span>{facility.name}</span>
          </li>
        )}
        {cityState && (
          <li className={chrome.fact}>
            <PlaceIcon aria-hidden="true" />
            <span>{cityState}</span>
          </li>
        )}
        <li className={chrome.fact}>
          <ScheduleIcon aria-hidden="true" />
          <span>{durationText(lessonType.durationInMinutes)}</span>
        </li>
        {slotLabel && (
          <li className={clsx(chrome.fact, classes.factStrong)}>
            <CalendarTodayIcon aria-hidden="true" />
            <span>{slotLabel}</span>
          </li>
        )}
      </ul>
      {totalLabel && (
        <>
          <hr className={chrome.summaryDivider} />
          <div className={chrome.totalRow}>
            <span className={chrome.totalLabel}>Total</span>
            <Typography
              variant="h5"
              component="span"
              className={chrome.totalValue}
            >
              {totalLabel}
            </Typography>
          </div>
          {totalNote && <p className={classes.totalNote}>{totalNote}</p>}
        </>
      )}
      {showCallout && (
        <CancellationCallout
          canStudentCancel={coach.canStudentCancel}
          cancellationPolicy={coach.cancellationPolicy}
          className={classes.summaryCallout}
        />
      )}
    </aside>
  )
}

ReviewSummaryCard.propTypes = {
  lessonType: PropTypes.object.isRequired,
  coach: PropTypes.object.isRequired,
  facility: PropTypes.object,
  slotLabel: PropTypes.string,
  totalLabel: PropTypes.string,
  totalNote: PropTypes.string,
  showCallout: PropTypes.bool,
}

ReviewSummaryCard.defaultProps = {
  showCallout: true,
}

export default ReviewSummaryCard
